import type { PrepCenterInput, SupplierInput } from "./api.js";
import type { PrepCenter, Supplier, SupplierSku } from "../types.js";

/**
 * Validação dos cadastros com tabela de SKUs — Fornecedores (§8.2) e Prep Center
 * (§8.3). Os dois compartilham Nome · Endereço · tabela de SKUs e diferem só no
 * campo "extra": Lead Time (inteiro ≥ 0) no fornecedor, Telefone (texto) no Prep
 * Center. Modelo de formulário UNIFICADO (`extra` é o campo variável) para uma
 * tela genérica. Lógica PURA (sem React), testável. A microcopy espelha verbatim
 * os schemas Zod do backend (apps/api/src/suppliers.ts e prep-centers.ts).
 */
export type CadastroKind = "supplier" | "prepCenter";

export interface SkuFormRow {
  sku: string;
  asin: string;
  description: string;
  /** Média de pedidos 30d — entrada bruta (texto), convertida no submit. */
  avgOrders30d: string;
}

export interface SkuRowErrors {
  sku?: string;
  asin?: string;
  avgOrders30d?: string;
}

export interface CadastroFormValues {
  name: string;
  address: string;
  /** Lead Time (string) p/ fornecedor; Telefone p/ Prep Center. */
  extra: string;
  skus: SkuFormRow[];
}

export interface CadastroFormErrors {
  name?: string;
  address?: string;
  extra?: string;
  skus: SkuRowErrors[];
}

export function emptySkuRow(): SkuFormRow {
  return { sku: "", asin: "", description: "", avgOrders30d: "" };
}

export function emptyCadastroForm(): CadastroFormValues {
  return { name: "", address: "", extra: "", skus: [] };
}

/** Converte um SupplierSku (numérico) na linha de formulário (texto). */
export function skuToFormRow(sku: SupplierSku): SkuFormRow {
  return {
    sku: sku.sku,
    asin: sku.asin,
    description: sku.description,
    avgOrders30d: String(sku.avgOrders30d)
  };
}

export function cadastroFromSupplier(supplier: Supplier): CadastroFormValues {
  return {
    name: supplier.name,
    address: supplier.address,
    extra: String(supplier.leadTimeDays),
    skus: supplier.skus.map(skuToFormRow)
  };
}

export function cadastroFromPrepCenter(prepCenter: PrepCenter): CadastroFormValues {
  return {
    name: prepCenter.name,
    address: prepCenter.address,
    extra: prepCenter.phone,
    skus: prepCenter.skus.map(skuToFormRow)
  };
}

export function validateSkuRow(row: SkuFormRow): SkuRowErrors {
  const errors: SkuRowErrors = {};
  if (row.sku.trim().length === 0) {
    errors.sku = "SKU é obrigatório.";
  }
  if (row.asin.trim().length === 0) {
    errors.asin = "ASIN é obrigatório.";
  }
  const value = Number(row.avgOrders30d);
  if (
    row.avgOrders30d.trim().length === 0 ||
    Number.isNaN(value) ||
    value < 0
  ) {
    errors.avgOrders30d = "Média de pedidos inválida.";
  }
  return errors;
}

function rowHasError(errors: SkuRowErrors): boolean {
  return (
    errors.sku !== undefined ||
    errors.asin !== undefined ||
    errors.avgOrders30d !== undefined
  );
}

function toSupplierSkus(rows: SkuFormRow[]): SupplierSku[] {
  return rows.map((row) => ({
    sku: row.sku.trim(),
    asin: row.asin.trim(),
    description: row.description.trim(),
    avgOrders30d: Number(row.avgOrders30d)
  }));
}

/** Lead Time: inteiro ≥ 0 editado como texto (mensagens verbatim do backend). */
function validateLeadTime(raw: string): string | undefined {
  if (raw.trim().length === 0) {
    return "Informe o lead time (dias).";
  }
  const value = Number(raw);
  if (Number.isNaN(value) || value < 0) {
    return "Lead time inválido.";
  }
  if (!Number.isInteger(value)) {
    return "Lead time deve ser inteiro.";
  }
  return undefined;
}

export function validateCadastroForm(
  kind: CadastroKind,
  values: CadastroFormValues
): { errors: CadastroFormErrors; valid: boolean } {
  const errors: CadastroFormErrors = {
    skus: values.skus.map(validateSkuRow)
  };
  if (values.name.trim().length === 0) {
    errors.name =
      kind === "supplier"
        ? "Informe o nome do fornecedor."
        : "Informe o nome do Prep Center.";
  }
  if (values.address.trim().length === 0) {
    errors.address = "Informe o endereço.";
  }
  if (kind === "supplier") {
    const leadTimeError = validateLeadTime(values.extra);
    if (leadTimeError !== undefined) {
      errors.extra = leadTimeError;
    }
  } else if (values.extra.trim().length === 0) {
    errors.extra = "Informe o telefone.";
  }

  const valid =
    errors.name === undefined &&
    errors.address === undefined &&
    errors.extra === undefined &&
    errors.skus.every((row) => !rowHasError(row));

  return { errors, valid };
}

/** Constrói o payload de fornecedor (assume `validateCadastroForm` válido). */
export function buildSupplierInput(values: CadastroFormValues): SupplierInput {
  return {
    name: values.name.trim(),
    address: values.address.trim(),
    leadTimeDays: Number(values.extra),
    skus: toSupplierSkus(values.skus)
  };
}

/** Constrói o payload de Prep Center (assume `validateCadastroForm` válido). */
export function buildPrepCenterInput(values: CadastroFormValues): PrepCenterInput {
  return {
    name: values.name.trim(),
    address: values.address.trim(),
    phone: values.extra.trim(),
    skus: toSupplierSkus(values.skus)
  };
}
